import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import type RTKClient from "@cloudflare/realtimekit";
import { HOST_AUDIO_CAPTURE_CONSTRAINTS } from "./realtimeKitPresets";

function stopStreamTracks({ stream }: { stream: MediaStream | null }) {
  stream?.getTracks().forEach((track) => track.stop());
}

function isMediaTrackLive(track: MediaStreamTrack | null): boolean {
  return track?.readyState === "live";
}

async function acquireHostMediaStream(): Promise<MediaStream | null> {
  const attempts = 3;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: HOST_AUDIO_CAPTURE_CONSTRAINTS,
      });
    } catch {
      if (attempt === attempts - 1) return null;
      await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)));
    }
  }

  return null;
}

async function replaceHostMediaStream({
  streamRef,
  isCancelled,
}: {
  streamRef: MutableRefObject<MediaStream | null>;
  isCancelled?: () => boolean;
}): Promise<{
  videoTrack: MediaStreamTrack | null;
  audioTrack: MediaStreamTrack | null;
  ready: boolean;
}> {
  stopStreamTracks({ stream: streamRef.current });
  streamRef.current = null;

  await new Promise((resolve) => setTimeout(resolve, 0));

  const stream = await acquireHostMediaStream();
  if (isCancelled?.()) {
    stopStreamTracks({ stream });
    return { videoTrack: null, audioTrack: null, ready: false };
  }

  streamRef.current = stream;
  return {
    videoTrack: stream?.getVideoTracks()[0] ?? null,
    audioTrack: stream?.getAudioTracks()[0] ?? null,
    ready: Boolean(stream),
  };
}

export function useHostMediaStream({
  meeting,
  previewVideoEnabled,
}: {
  meeting: RTKClient | undefined;
  previewVideoEnabled: boolean;
}) {
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [videoTrack, setVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [audioTrack, setAudioTrack] = useState<MediaStreamTrack | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!meeting) {
      setReady(false);
      setVideoTrack(null);
      setAudioTrack(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      const { videoTrack: nextVideoTrack, audioTrack: nextAudioTrack, ready: nextReady } =
        await replaceHostMediaStream({
          streamRef,
          isCancelled: () => cancelled,
        });
      if (cancelled) return;
      setVideoTrack(nextVideoTrack);
      setAudioTrack(nextAudioTrack);
      setReady(nextReady);
    })();

    return () => {
      cancelled = true;
    };
  }, [meeting]);

  const refreshMediaStream = useCallback(async () => {
    const { videoTrack: nextVideoTrack, audioTrack: nextAudioTrack, ready: nextReady } =
      await replaceHostMediaStream({ streamRef });
    setVideoTrack(nextVideoTrack);
    setAudioTrack(nextAudioTrack);
    setReady(nextReady);
    return { videoTrack: nextVideoTrack, audioTrack: nextAudioTrack };
  }, []);

  const bindPreviewVideo = useCallback(
    (el: HTMLVideoElement | null) => {
      videoElementRef.current = el;
      if (!el) return;

      if (!previewVideoEnabled || !videoTrack) {
        el.srcObject = null;
        return;
      }

      el.srcObject = new MediaStream([videoTrack]);
      void el.play().catch(() => {});
    },
    [previewVideoEnabled, videoTrack],
  );

  useEffect(() => {
    bindPreviewVideo(videoElementRef.current);
  }, [bindPreviewVideo]);

  useEffect(() => {
    return () => {
      stopStreamTracks({ stream: streamRef.current });
      streamRef.current = null;
    };
  }, []);

  return { videoRef: bindPreviewVideo, videoTrack, audioTrack, ready, refreshMediaStream };
}

export { isMediaTrackLive };
